import { Component, type ReactNode } from 'react';
import { ERR } from '../i18n/it';
import { useDisplaySelector } from '../realtime/hooks';
import { FullscreenMessage } from './FullscreenMessage';
import { Panel } from './Panel';

interface BoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  /** Quando cambia, un pannello andato in errore riprova a disegnarsi. */
  resetKey?: unknown;
}

class Boundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error('[TIW] errore di disegno', error);
  }

  componentDidUpdate(prev: BoundaryProps) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey)
      this.setState({ failed: false });
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** Un pannello che riceve un dato fuori contratto mostra un ripiego; gli altri restano al loro posto. */
export function PanelBoundary({
  children,
  className = 'flex-1',
}: {
  children: ReactNode;
  /** Dimensioni del ripiego: le stesse del pannello che sostituisce. */
  className?: string;
}) {
  const data = useDisplaySelector((s) => s.data);
  return (
    <Boundary
      resetKey={data}
      fallback={
        <Panel className={`flex items-center justify-center text-subtitle text-faint ${className}`}>
          {ERR.panel}
        </Panel>
      }
    >
      {children}
    </Boundary>
  );
}

/** Ultima rete: se un intero schermo si rompe, il kiosk si ricarica da solo dopo un minuto. */
export class RootBoundary extends Component<
  { children: ReactNode; onReload?: () => void },
  { failed: boolean }
> {
  state = { failed: false };
  private timer: number | undefined;

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error('[TIW] errore dello schermo', error);
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(
      () => (this.props.onReload ?? (() => window.location.reload()))(),
      60_000,
    );
  }

  componentWillUnmount() {
    window.clearTimeout(this.timer);
  }

  render() {
    return this.state.failed ? <FullscreenMessage {...ERR.root} /> : this.props.children;
  }
}
